import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInflightSince, setUserOfflineMode } from '../../services/networkStatus';
import { useIsFlushing, usePendingWorkUnits } from '../../hooks/useOfflineSync';
import './SlowConnectionBanner.css';

// Umbrales de aparición — dos niveles de visibilidad creciente:
//  - 12s: aparece el banner discreto. Por debajo del timeout general de
//    axios (15s), para que dé tiempo a verlo también en las peticiones
//    normales antes de que fallen.
//  - 20s: el botón "Trabajar offline" se vuelve prominente (rojo).
const SLOW_THRESHOLD_MS = 12000;
const URGENT_THRESHOLD_MS = 20000;

// Banner sticky bajo el header con dos modos:
//   - Modo "conexión lenta sin cola": petición lenta inflight + no hay
//     pendientes → mensaje + botón "Trabajar offline".
//   - Modo "subiendo cola": isFlushing true → barra de progreso real
//     basada en unidades de trabajo (items + fotos). Se mantiene visible
//     toda la sesión, no parpadea entre POST y POST. No ofrece el botón
//     "Trabajar offline" — interrumpir la propia subida no tiene sentido.
export function SlowConnectionBanner() {
  const { t } = useTranslation();
  const startedAt = useInflightSince();
  const flushing = useIsFlushing();
  const workUnits = usePendingWorkUnits();
  const [, force] = useState(0);

  // High-water-mark de las unidades de trabajo en la sesión actual: subimos
  // si llegan nuevos pendientes; lo reseteamos a 0 cuando la cola queda
  // vacía. Permite mostrar un porcentaje estable que avanza monótonamente
  // aunque el usuario añada más cargas durante el sync.
  const sessionMaxRef = useRef(0);
  if (workUnits > sessionMaxRef.current) sessionMaxRef.current = workUnits;
  if (workUnits === 0 && sessionMaxRef.current !== 0) sessionMaxRef.current = 0;

  // Re-render cada 500ms para actualizar el contador de tiempo de
  // "conexión lenta" y el umbral de gravedad.
  useEffect(() => {
    if (startedAt == null && !flushing) return;
    const id = setInterval(() => force((n) => n + 1), 500);
    return () => clearInterval(id);
  }, [startedAt, flushing]);

  const elapsedMs = startedAt != null ? Date.now() - startedAt : 0;
  const slow = startedAt != null && elapsedMs >= SLOW_THRESHOLD_MS;
  const uploading = flushing && workUnits > 0;

  // El banner aparece si: hay petición lenta (>3s) O hay sesión de sync
  // activa con trabajo real. Sin ninguno de los dos, nada que mostrar.
  if (!slow && !uploading) return null;

  const urgent = slow && elapsedMs >= URGENT_THRESHOLD_MS && !uploading;
  const total = sessionMaxRef.current;
  const done = Math.max(0, total - workUnits);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const goOffline = () => {
    setUserOfflineMode(true);
  };

  return (
    <div className={`slow-banner ${urgent ? 'slow-banner--urgent' : ''}`} role="status">
      <div className="slow-banner__msg">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        {uploading ? (
          <span>
            {t('components.slowUploading')} <strong>{done}/{total}</strong> ({pct}%)
          </span>
        ) : (
          <span>{urgent ? t('components.slowConnectionUrgent') : t('components.slowConnection')}</span>
        )}
      </div>
      {!uploading && (
        <button type="button" className="slow-banner__btn" onClick={goOffline}>
          {t('components.workOffline')}
        </button>
      )}
    </div>
  );
}
